import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router-dom';
import { PageLayout } from 'components/PageLayout';
import { PlaceForm } from 'components/PlaceForm';
import { PlaceAddress } from 'features/places/places.types';
import { useLocationState } from 'hooks/useLocationState';
import { SimplePageLayout } from 'components/layouts/SimplePageLayout';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { usePlaces } from 'features/places/hooks/usePlaces';
import { MEMBERSHIP_PLANS } from 'features/membership/membership.constants';
import { getPaywallLink } from 'features/membership/membership.utils';

const Content = (): JSX.Element => {
  const { t } = useTranslation();
  const { locationState: placeAddress } = useLocationState<PlaceAddress>();
  // A prefilled address always arrives with coordinates; treat it as complete.
  const initialAddress = placeAddress.coordinates
    ? (placeAddress as PlaceAddress)
    : undefined;

  return (
    <>
      <Helmet>
        <title>{t('addPlace.title')}</title>
      </Helmet>
      <SimplePageLayout>
        <PlaceForm initialAddress={initialAddress} />
      </SimplePageLayout>
    </>
  );
};

export const AddPlace = (): JSX.Element => {
  const { t } = useTranslation();
  const { fromRoute } = useLocationState('/');
  const pinImprovements = useReleaseFlag('pin_drop_improvements');
  const { canAddPlace } = usePlaces();

  if (!canAddPlace) {
    const paywallLink = getPaywallLink({
      focusedPlan: MEMBERSHIP_PLANS.yearly,
      source: 'places_page_add_place',
    });
    return <Redirect to={paywallLink.url} />;
  }

  return (
    <PageLayout
      data-cname="AddPlacePage"
      headerBarProps={{
        title: t('addPlace.title'),
        action: pinImprovements ? fromRoute : '/',
      }}
    >
      <Content />
    </PageLayout>
  );
};
